/* eslint-disable */
import { useEffect, useState } from "react";
import { useLocation, NavLink, useNavigate } from "react-router-dom";
import PropTypes from "prop-types";

// Sessions
import Session from "utils/Sessions";

// @mui material components
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import Icon from "@mui/material/Icon";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Material Dashboard 2 React example components
import SidenavCollapse from "examples/Sidenav/SidenavCollapse";
import SidenavRoot from "examples/Sidenav/SidenavRoot";

// Logo
import logo from "assets/images/sidenav_logo.svg";

// Material Dashboard 2 React context
import {
  useMaterialUIController,
  setMiniSidenav,
  setTransparentSidenav,
  setWhiteSidenav,
} from "context";

import Confirmation from "examples/modal/Confirmation/Confirmation";
import { useDispatch } from "react-redux";
import Constants, { defaultData } from "utils/Constants";
import { resetStateThunk } from "redux/Thunks/Authentication";
import pxToRem from "assets/theme/functions/pxToRem";

function Sidenav({ color, brand, brandName, routes, role, ...rest }) {
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, transparentSidenav, whiteSidenav, darkMode } = controller;
  const [openLogout, setOpenLogout] = useState(false);
  const [switchAccount, setSwitchAccount] = useState(false);

  // Accordion state for Lead Bay collapsible menu
  const [leadBayOpen, setLeadBayOpen] = useState(true);

  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = location.pathname;
  const collapseName = location.pathname.replace("/", "");
  const dispatchThunk = useDispatch();

  const handleOpenLogout = () => {
    setOpenLogout(true);
  };

  const handleLogout = async () => {
    Session.setClear();
    navigate("/authentication/sign-in", { replace: true });
    await dispatchThunk(resetStateThunk());
  };

  const handleAccountSwitch = () => {
    setSwitchAccount(false);
    Session.setLogoutSuperadminAsAdmin();
    navigate("admin/home", { replace: true });
  };

  let textColor = "white";
  if (transparentSidenav || (whiteSidenav && !darkMode)) {
    textColor = "dark";
  } else if (whiteSidenav && darkMode) {
    textColor = "inherit";
  }

  const handleCloseMiniSidenav = () => setMiniSidenav(dispatch, !miniSidenav);

  useEffect(() => {
    function handleMiniSidenav() {
      setMiniSidenav(dispatch, window.innerWidth < 1200);
      setTransparentSidenav(dispatch, window.innerWidth < 1200 ? false : transparentSidenav);
      setWhiteSidenav(dispatch, window.innerWidth < 1200 ? false : whiteSidenav);
    }
    window.addEventListener("resize", handleMiniSidenav);
    handleMiniSidenav();
    return () => window.removeEventListener("resize", handleMiniSidenav);
  }, [dispatch, location]);

  // Role verification (Admin role only for Partners and Mobile Users)
  const roleLower = (role || "").toLowerCase();
  const isAdmin =
    roleLower === (defaultData.ADMIN_ROLE || "admin").toLowerCase() ||
    roleLower === (defaultData.SUPER_ADMIN_ROLE || "superadmin").toLowerCase();

  // Filter routes: Group parent Lead Bay items
  const parentLeadBayItems = routes.filter((r) => r.parent === "Lead Bay");

  // Specific route lookups matching requested sidebar order
  const dashboardRoute = routes.find((r) => r.key === "client");
  const inquiryRoute = routes.find(
    (r) => r.key === "client/inquiries" || r.route === "/client/inquiries"
  );
  const emiRoute = routes.find(
    (r) => r.key === "client/emi-calculator" || r.route === "/client/emi-calculator"
  );
  const creditScoreRoute = routes.find(
    (r) => r.key === "client/credit-score" || r.route === "/client/credit-score"
  );
  const partnersRoute = routes.find(
    (r) => r.key === "client/partners" || r.route === "/client/partners"
  );
  const mobileUsersRoute = routes.find(
    (r) => r.key === "client/mobileusers" || r.route === "/client/mobileusers"
  );
  const aboutUsRoute = routes.find(
    (r) => r.key === "client/about-us" || r.route === "/client/about-us"
  );

  const isLeadBayActive =
    currentPath.includes("/client/lead-bay") ||
    currentPath.includes("/client/bank-account-opening") ||
    currentPath.includes("/client/insurance") ||
    currentPath.includes("/client/bill");

  return (
    <SidenavRoot
      {...rest}
      variant="permanent"
      ownerState={{ transparentSidenav, whiteSidenav, miniSidenav, darkMode }}
      sx={{
        backgroundColor: "#FFFFFF",
        borderRight: "1px solid #EAECF0",
      }}
    >
      <MDBox pb={0} textAlign="center" sx={{ backgroundColor: "transparent", padding: 1.5 }}>
        <MDBox
          component={NavLink}
          to="/client"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          {brand && (
            <MDBox component="img" src={logo} alt="Brand" sx={{ width: "85%", height: pxToRem(47) }} />
          )}
        </MDBox>
      </MDBox>

      <Divider sx={{ marginTop: 0, marginBottom: 1, backgroundColor: "#EAECF0" }} />

      <List sx={{ px: 1, marginTop: 0.5 }}>
        {/* 1. Dashboard */}
        {dashboardRoute && (
          <NavLink key={dashboardRoute.key} to={dashboardRoute.route} style={{ textDecoration: "none" }}>
            <SidenavCollapse
              name={dashboardRoute.name}
              icon={dashboardRoute.icon}
              active={currentPath === dashboardRoute.route || currentPath === "/client"}
            />
          </NavLink>
        )}

        {/* 2. Inquiry */}
        {inquiryRoute && (
          <NavLink key={inquiryRoute.key} to={inquiryRoute.route} style={{ textDecoration: "none" }}>
            <SidenavCollapse
              name={inquiryRoute.name}
              icon={inquiryRoute.icon}
              active={currentPath === inquiryRoute.route}
            />
          </NavLink>
        )}

        {/* 3. Collapsible Lead Bay Category */}
        <MDBox onClick={() => setLeadBayOpen(!leadBayOpen)}>
          <SidenavCollapse
            name="Lead Bay"
            icon="assessment"
            active={isLeadBayActive}
            isExpandable={true}
            isExpanded={leadBayOpen}
          />
        </MDBox>

        {/* Lead Bay Sub-items Accordion with Continuous Vertical Left Line */}
        <Collapse in={leadBayOpen} timeout="auto" unmountOnExit>
          <MDBox
            sx={{
              position: "relative",
              ml: 3.2,
              pl: 0.5,
              borderLeft: "2px solid #EAECF0",
            }}
          >
            <List component="div" disablePadding>
              {parentLeadBayItems.map((child) => (
                <NavLink key={child.key} to={child.route} style={{ textDecoration: "none" }}>
                  <SidenavCollapse
                    name={child.name}
                    icon={null}
                    active={currentPath === child.route}
                    isChild={true}
                  />
                </NavLink>
              ))}
            </List>
          </MDBox>
        </Collapse>

        {/* 4. EMI Calculator */}
        {emiRoute && (
          <NavLink key={emiRoute.key} to={emiRoute.route} style={{ textDecoration: "none" }}>
            <SidenavCollapse
              name={emiRoute.name}
              icon={emiRoute.icon}
              active={currentPath === emiRoute.route}
            />
          </NavLink>
        )}

        {/* 5. Credit Score */}
        {creditScoreRoute && (
          <NavLink key={creditScoreRoute.key} to={creditScoreRoute.route} style={{ textDecoration: "none" }}>
            <SidenavCollapse
              name={creditScoreRoute.name}
              icon={creditScoreRoute.icon}
              active={currentPath === creditScoreRoute.route}
            />
          </NavLink>
        )}

        {/* 6. Partners (Admin role only) */}
        {isAdmin && partnersRoute && (
          <NavLink key={partnersRoute.key} to={partnersRoute.route} style={{ textDecoration: "none" }}>
            <SidenavCollapse
              name={partnersRoute.name}
              icon={partnersRoute.icon}
              active={currentPath === partnersRoute.route}
            />
          </NavLink>
        )}

        {/* 7. Mobile Users (Admin role only) */}
        {isAdmin && mobileUsersRoute && (
          <NavLink key={mobileUsersRoute.key} to={mobileUsersRoute.route} style={{ textDecoration: "none" }}>
            <SidenavCollapse
              name={mobileUsersRoute.name}
              icon={mobileUsersRoute.icon}
              active={currentPath === mobileUsersRoute.route}
            />
          </NavLink>
        )}

        {/* 8. About Us */}
        {aboutUsRoute && (
          <NavLink key={aboutUsRoute.key} to={aboutUsRoute.route} style={{ textDecoration: "none" }}>
            <SidenavCollapse
              name={aboutUsRoute.name}
              icon={aboutUsRoute.icon}
              active={currentPath === aboutUsRoute.route}
            />
          </NavLink>
        )}

        {/* Any other miscellaneous top-level collapse routes */}
        {routes
          .filter(
            (r) =>
              r.type === "collapse" &&
              !r.parent &&
              ![
                "client",
                "client/inquiries",
                "client/lead-bay-home",
                "client/lead-bay",
                "client/emi-calculator",
                "client/credit-score",
                "client/partners",
                "client/mobileusers",
                "client/about-us",
                "client/dashboard-v2",
              ].includes(r.key)
          )
          .map((r) => (
            <NavLink key={r.key} to={r.route} style={{ textDecoration: "none" }}>
              <SidenavCollapse name={r.name} icon={r.icon} active={currentPath === r.route} />
            </NavLink>
          ))}
      </List>

      {/* Footer / Toggle & Logout */}
      <MDBox p={1} mt="auto" display="flex" justifyContent="center" flexDirection="column">
        <IconButton
          size="small"
          disableRipple
          onClick={handleCloseMiniSidenav}
          sx={{ color: "#667085", mb: 1 }}
        >
          <Icon fontSize="medium">
            {miniSidenav ? "keyboard_double_arrow_right" : "keyboard_double_arrow_left"}
          </Icon>
        </IconButton>

        {Session.isSuperAdminViewingAdminPanel && (
          <MDBox onClick={() => setSwitchAccount(true)}>
            <SidenavCollapse name="Switch To Super Admin" icon="switch_account" parent="" />
          </MDBox>
        )}

        <MDBox onClick={handleOpenLogout}>
          <SidenavCollapse name="Logout" icon="power_settings_new" parent="" />
        </MDBox>

        {openLogout && (
          <Confirmation
            open={openLogout}
            title={Constants.USER_LOGOUT_TITTLE}
            message={Constants.LOGOUT_MESSAGE}
            handleClose={() => setOpenLogout(false)}
            handleAction={handleLogout}
          />
        )}

        {switchAccount && (
          <Confirmation
            open={switchAccount}
            title={Constants.SWITCH_ACCOUNT_TITTLE}
            message={Constants.SWITCH_SUPERADMIN_ACCOUNT_TO_ADMIN_MESSAGE}
            handleClose={() => setSwitchAccount(false)}
            handleAction={handleAccountSwitch}
          />
        )}
      </MDBox>
    </SidenavRoot>
  );
}

Sidenav.defaultProps = {
  color: "info",
  brand: "",
};

Sidenav.propTypes = {
  color: PropTypes.oneOf(["primary", "secondary", "info", "success", "warning", "error", "dark"]),
  brand: PropTypes.string,
  role: PropTypes.string.isRequired,
  brandName: PropTypes.string.isRequired,
  routes: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default Sidenav;
